import { categoryPath } from "./categoryTree";

/**
 * The shape the header renders, and the two ways it can be produced.
 *
 * Navigation is authored in admin and served resolved by `/navigation/tree`:
 * a root entry carries its own mega-menu columns, each column its links. That
 * is the source of truth. But a fresh database has no rows yet and the fetch
 * can fail, and a storefront whose menu is empty is worse than one built from
 * what the catalogue already knows — so `fallbackFromCategories` derives the
 * same shape from the category tree, which is what the header showed before
 * navigation became editable.
 *
 * Both paths end in one structure, so nothing downstream has to know which of
 * the two it is looking at:
 *
 *   { key, to, title, badge, accent, mega, feature, columns: [{ key, title, to, links: [...] }] }
 */

/* Still a whitelist: it is what keeps an admin typo like `https://…` or
   `/admin/orders` out of the storefront header. */
const SAFE_EXACT = [
  "/", "/falcon-society", "/journal", "/blog", "/shop", "/outlet",
  "/endirim", "/collections", "/about", "/contact",
  "/delivery", "/returns", "/gallery",
];
const SAFE_PREFIX = ["/category/", "/categories/", "/collections/", "/tags/", "/journal/", "/blog/"];

export function isSafeNavLink(link = "") {
  const value = String(link || "").split("?")[0];
  if (!value.startsWith("/")) return false;
  if (value.startsWith("//")) return false;      // protocol-relative is off-site
  return SAFE_EXACT.includes(value) || SAFE_PREFIX.some((prefix) => value.startsWith(prefix));
}

function link(node) {
  const to = node?.link || "";
  return isSafeNavLink(to) ? to : "";
}

/** One mega-menu column: a heading, optionally itself a link, plus its links. */
function toColumn(node) {
  const links = (node.children || [])
    .map((child) => ({ key: child.id, to: link(child), title: child.title, badge: child.badge }))
    .filter((child) => child.to);
  const heading = link(node);
  if (!links.length && !heading) return null;
  return { key: node.id, title: node.title, to: heading, links };
}

/** Normalise one resolved API node into what the header renders. */
function toItem(node) {
  const columns = (node.children || []).map(toColumn).filter(Boolean);
  const to = link(node);
  // A column-only parent with no address of its own still opens its menu; a
  // plain entry with neither is not renderable at all.
  if (!to && !columns.length) return null;
  const feature = node.feature?.image_url || node.feature?.title ? node.feature : null;
  return {
    key: node.id,
    to,
    title: node.title,
    badge: node.badge,
    accent: node.accent || null,
    mega: Boolean(node.mega && columns.length),
    feature,
    columns,
  };
}

export function normaliseNavigation(rows) {
  return (rows || []).map(toItem).filter(Boolean);
}

/**
 * The menu the catalogue implies, for when admin has not authored one.
 *
 * Kompakt panel qaydası (2026-10-05): bir sütun — bölmənin əsas
 * kateqoriyaları, alt kateqoriyasız. `/shop` bütün geyim kökləri (öz
 * bəndi olan Ayaqqabı/Aksesuar və «Yeni gələnlər» rəfi xaric), bir
 * kateqoriya bəndi isə öz uşaqlarını açır. Alt səviyyə panelə düşmür —
 * o, kateqoriya səhifəsində başlığın altındakı link sətridir.
 */
const OWN_TOP_LEVEL = ["ayaqqabi", "aksesuar"];
const NOT_A_SHELF = ["yeni-gelenler"];

export function fallbackColumnsFor(item, categoryTree) {
  if (!categoryTree?.length) return [];
  const to = item?.to || "";
  const asLink = (node) => ({ key: node.id, to: categoryPath(node.handle), title: node.title });

  if (to === "/shop") {
    const mains = categoryTree.filter(
      (node) => !OWN_TOP_LEVEL.includes(node.handle) && !NOT_A_SHELF.includes(node.handle),
    );
    if (!mains.length) return [];
    return [{
      key: "shop",
      title: { az: "Geyimlər", en: "Clothing" },
      to: "/shop",
      links: mains.map(asLink),
    }];
  }
  if (!to.startsWith("/categories/")) return [];
  const handle = to.slice("/categories/".length);
  const department = categoryTree.find((node) => node.handle === handle);
  if (!department?.children?.length) return [];
  return [{
    key: department.id,
    title: department.title,
    to: categoryPath(department.handle),
    links: department.children.map(asLink),
  }];
}

/**
 * Columns for the entry that is currently open.
 *
 * Admin-authored columns win; a department entry with none falls back to the
 * catalogue so the panel is never empty just because nobody filled it in.
 */
export function columnsFor(item, categoryTree) {
  if (!item) return [];
  if (item.columns?.length) return item.columns;
  return fallbackColumnsFor(item, categoryTree);
}

/** Does this entry open a panel at all? */
export function opensPanel(item, categoryTree) {
  if (!item) return false;
  if (item.mega) return true;
  return columnsFor(item, categoryTree).length > 0;
}
